import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Calendar, Clock, Flag, Compass, ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PROTECT_OPTIONS = ["Golf", "Sport", "Famille", "Écriture", "Lecture", "Temps libre"];
const HELP_OPTIONS = [
  "Protéger mon temps", "Rester au top des emails", "Planifier ma semaine",
  "Éviter les deadlines manquées", "Tout ça à la fois",
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [hours, setHours] = useState({ start: "09:00", end: "18:30" });
  const [protect, setProtect] = useState(["Golf"]);
  const [help, setHelp] = useState(["Tout ça à la fois"]);

  const connectGoogle = async () => {
    setConnecting(true);
    try {
      const res = await api.get("/gmail/oauth/start");
      window.location.href = res.data.url;
    } catch {
      toast.error("Connexion Google indisponible");
      setConnecting(false);
    }
  };

  const toggle = (arr, set, v) =>
    set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const finish = async () => {
    setSaving(true);
    try {
      await api.put("/rules", {
        working_hours_start: hours.start,
        working_hours_end: hours.end,
        personal: { protect_for: protect },
        help_focus: help,
      });
      await api.post("/onboarding/complete");
      setUser({ ...user, onboarded: true });
      navigate("/", { replace: true });
    } catch {
      toast.error("Erreur lors de l'enregistrement");
      setSaving(false);
    }
  };

  const steps = [
    {
      icon: Mail, title: "Connecte ta boîte mail",
      desc: "AIRTIME analyse tes emails Gmail pour en extraire tâches, échéances et relances. Rien n'est envoyé sans ta validation.",
      body: (
        <button data-testid="onboarding-connect-gmail" onClick={connectGoogle} disabled={connecting}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-primary-foreground font-medium hover:opacity-90 transition-all">
          {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Connecter Gmail + Calendar
        </button>
      ),
    },
    {
      icon: Calendar, title: "Ton agenda",
      desc: "En connectant Google Calendar, AIRTIME détecte tes vrais créneaux libres. Tant qu'aucun compte n'est connecté, l'application reste vide (aucune donnée fictive).",
      body: (
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <Check className="w-4 h-4 text-emerald-400" /> Tu pourras connecter Google à tout moment dans Réglages → Ajouter un compte.
        </div>
      ),
    },
    {
      icon: Clock, title: "Quand travailles-tu ?",
      desc: "AIRTIME respectera ces horaires pour ne jamais surcharger tes journées.",
      body: (
        <div className="flex items-center gap-4">
          {["start", "end"].map((k) => (
            <div key={k}>
              <div className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">{k === "start" ? "Début" : "Fin"}</div>
              <input data-testid={`onboarding-hours-${k}`} type="time" value={hours[k]}
                onChange={(e) => setHours({ ...hours, [k]: e.target.value })}
                className="bg-secondary border border-border rounded-xl px-4 py-2.5 text-sm" />
            </div>
          ))}
        </div>
      ),
    },
    {
      icon: Flag, title: "Quel temps veux-tu protéger ?",
      desc: "AIRTIME défendra activement ces moments — le but n'est pas de remplir chaque trou de ton agenda.",
      body: (
        <div className="flex flex-wrap gap-2.5">
          {PROTECT_OPTIONS.map((o) => (
            <button key={o} data-testid={`onboarding-protect-${o}`} onClick={() => toggle(protect, setProtect, o)}
              className={`rounded-full px-4 py-2 text-sm border transition-all ${protect.includes(o)
                ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "border-border text-muted-foreground hover:text-foreground"}`}>
              {o}
            </button>
          ))}
        </div>
      ),
    },
    {
      icon: Compass, title: "Comment puis-je t'aider ?",
      desc: "Choisis ta priorité principale. Tu pourras l'ajuster à tout moment.",
      body: (
        <div className="flex flex-wrap gap-2.5">
          {HELP_OPTIONS.map((o) => (
            <button key={o} data-testid={`onboarding-help-${o}`} onClick={() => toggle(help, setHelp, o)}
              className={`rounded-full px-4 py-2 text-sm border transition-all ${help.includes(o)
                ? "bg-primary/15 border-primary/40 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
              {o}
            </button>
          ))}
        </div>
      ),
    },
  ];

  const s = steps[step];
  const Icon = s.icon;
  const isLast = step === steps.length - 1;

  return (
    <div className="min-h-screen w-full bg-background text-foreground flex items-center justify-center p-6 grain">
      <div className="relative z-10 w-full max-w-lg">
        <div className="flex items-center gap-1.5 mb-8">
          {steps.map((_, i) => (
            <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i <= step ? "bg-primary" : "bg-border"}`} />
          ))}
        </div>
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.3 }}>
            <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-6">
              <Icon className="w-6 h-6" />
            </div>
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Étape {step + 1} / {steps.length}</div>
            <h1 className="font-display text-3xl font-semibold tracking-tight">{s.title}</h1>
            <p className="mt-3 text-muted-foreground leading-relaxed">{s.desc}</p>
            <div className="mt-7">{s.body}</div>
          </motion.div>
        </AnimatePresence>

        <div className="mt-10 flex items-center justify-between">
          <button data-testid="onboarding-skip" onClick={finish}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            Passer
          </button>
          <button
            data-testid="onboarding-next"
            onClick={() => (isLast ? finish() : setStep(step + 1))}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-primary-foreground font-medium hover:opacity-90 transition-all">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (isLast ? "Entrer dans AIRTIME" : "Continuer")}
            {!saving && <ArrowRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
